import * as React from 'react';

import { DataGridColumn } from '@rematter/ui-web';

import { InventoryPeriodRecord } from '../../_graphql/queryInventoryPeriods';
import { useTranslations } from '../../i18n/useTranslations';

import { PeriodStatusBadge } from './PeriodStatusBadge';

import {
  formatExclusiveEndDateShort,
  formatZonedDateTime,
  formatZonedDateTimeShort,
} from '~/shared/utils/dateTime';

type UseInventoryPeriodsColumnsArgs = {
  onRowClick?: (cell: { row: InventoryPeriodRecord }) => void;
};

const renderStatusCell = ({ row }: { row: InventoryPeriodRecord }) => (
  <PeriodStatusBadge status={row.status} />
);

const renderStartDateCell = ({ row }: { row: InventoryPeriodRecord }) => (
  <span className="text-sm">{formatZonedDateTimeShort(row.startDate)}</span>
);

const renderEndDateCell = ({ row }: { row: InventoryPeriodRecord }) => (
  <span className="text-sm">{formatExclusiveEndDateShort(row.endDate)}</span>
);

const renderLockedAtCell = ({ row }: { row: InventoryPeriodRecord }) => (
  <span className="text-sm">
    {formatZonedDateTime(row.lockedAt, { includeTime: true })}
  </span>
);

export const useInventoryPeriodsColumns = ({
  onRowClick,
}: UseInventoryPeriodsColumnsArgs = {}): DataGridColumn<InventoryPeriodRecord>[] => {
  const t = useTranslations();

  return React.useMemo(() => {
    const columns: DataGridColumn<InventoryPeriodRecord>[] = [
      {
        key: 'name',
        header: t.periodsListView.table.columns.name,
        onClick: onRowClick,
        cell: ({ row }) => <span className="font-medium">{row.name}</span>,
        width: 220,
        sortable: true,
      },
      {
        key: 'facility',
        header: t.periodsListView.table.columns.facility,
        onClick: onRowClick,
        cell: ({ row }) => <span className="text-sm">{row.facilityName}</span>,
        width: 180,
      },
      {
        key: 'status',
        header: t.periodsListView.table.columns.status,
        onClick: onRowClick,
        cell: renderStatusCell,
        width: 120,
        sortable: true,
      },
      {
        key: 'startDate',
        header: t.periodsListView.table.columns.startDate,
        onClick: onRowClick,
        cell: renderStartDateCell,
        width: 140,
        sortable: true,
      },
      {
        key: 'endDate',
        header: t.periodsListView.table.columns.endDate,
        onClick: onRowClick,
        cell: renderEndDateCell,
        width: 140,
        sortable: true,
      },
      {
        key: 'lockedAt',
        header: t.periodsListView.table.columns.lockedAt,
        onClick: onRowClick,
        cell: renderLockedAtCell,
        width: 180,
      },
    ];

    return columns;
  }, [t, onRowClick]);
};
